import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/ResearchCms";
import { LifecycleStepper } from "@/components/ResearchCms/LifecycleStepper/LifecycleStepper";
import type { EditorialReviewStatus, EditorialWriteStatus } from "../../types";
import { editorialLifecycleSteps } from "../../utils/editorialLifecycleSteps";
import styles from "./IndividualBanner.module.css";

type IndividualBannerProps = {
  lawyerName: string;
  contactUuid: string;
  hasSubmission: boolean;
  fileUri?: string | null;
  reviewStatus: EditorialReviewStatus | null;
  onStatusChange?: (status: EditorialWriteStatus) => void;
  canSetStatus?: (status: EditorialWriteStatus) => boolean;
  busy?: boolean;
  statusActionsDisabled?: boolean;
  showTranslationStep?: boolean;
  /** The owning edition's status, which promotes an approved lawyer to "Released" once it is live. */
  editionStatus?: string | null;
  organisationName?: string | null;
  organisationUuid?: string | null;
  officeId?: string | null;
  officeName?: string | null;
  templateType?: string | null;
  yearOfCall?: number | null;
  silkYear?: number | null;
};

export function IndividualBanner({
  lawyerName,
  contactUuid,
  hasSubmission,
  fileUri = null,
  reviewStatus,
  onStatusChange,
  canSetStatus,
  busy = false,
  statusActionsDisabled = false,
  showTranslationStep = false,
  editionStatus,
  organisationName,
  organisationUuid,
  officeId,
  officeName,
  templateType,
  yearOfCall,
  silkYear,
}: IndividualBannerProps) {
  const lifecycleSteps = editorialLifecycleSteps(showTranslationStep, editionStatus);
  const hasOrganisation = Boolean(organisationUuid?.trim() || organisationName?.trim());
  const organisationLabel =
    organisationName?.trim() && organisationUuid
      ? `${organisationName} (${organisationUuid})`
      : (organisationName?.trim() ?? organisationUuid ?? "");
  const hasOffice = Boolean(officeName?.trim());
  const isBarTemplate = templateType === "bar";

  return (
    <div className={styles.banner}>
      <div className={styles.bannerHeader}>
        <div className={styles.bannerLeft}>
          <div className={styles.bannerIcon} aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className={styles.bannerInfo}>
            <div className={styles.bannerTitleRow}>
              <h1 className={styles.bannerTitle}>
                <Link to={`/address-book/contacts/${contactUuid}`} className={styles.titleLink}>
                  {lawyerName}
                </Link>
              </h1>
              <span className={styles.uuidBadge} title="Contact id — address book">
                {contactUuid}
              </span>
            </div>
            {hasOrganisation && (
              <div className={styles.bannerOrganisation}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                  <path d="M9 22v-4h6v4" />
                  <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01" />
                </svg>
                {organisationUuid ? (
                  <Link
                    to={`/address-book/companies/${organisationUuid}`}
                    className={styles.titleLink}
                    title="Company id — address book"
                  >
                    {organisationLabel}
                  </Link>
                ) : (
                  <span>{organisationLabel}</span>
                )}
              </div>
            )}
            {hasOffice && (
              <div className={styles.bannerOrganisation}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {officeId ? (
                  <Link to={`/address-book/offices/${officeId}`} className={styles.titleLink}>
                    {officeName}
                  </Link>
                ) : (
                  <span>{officeName}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.bannerMeta}>
          {reviewStatus && (
            <StatusBadge
              status={reviewStatus}
              vocabulary="review-status"
              editionStatus={editionStatus}
            />
          )}
          <span
            className={`${styles.submissionBadge} ${hasSubmission ? styles.submitted : styles.notSubmitted}`}
          >
            {hasSubmission ? "Submitted" : "Not Submitted"}
          </span>
          {hasSubmission && fileUri && (
            <a
              href={fileUri}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewSubmissionButton}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              View Submission
            </a>
          )}
        </div>
      </div>

      {isBarTemplate && (yearOfCall != null || silkYear != null) && (
        <div className={styles.bannerMetaRow}>
          {isBarTemplate && yearOfCall != null && (
            <>
              <span className={styles.eyebrow}>Year of Call</span>
              <span className={styles.tierValue}>{yearOfCall}</span>
            </>
          )}
          {isBarTemplate && silkYear != null && (
            <>
              {yearOfCall != null && <span className={styles.divider} aria-hidden="true" />}
              <span className={styles.eyebrow}>Silk Year</span>
              <span className={styles.tierValue}>{silkYear}</span>
            </>
          )}
        </div>
      )}

      {reviewStatus && reviewStatus !== "deleted" && (
        <LifecycleStepper
          steps={lifecycleSteps}
          currentStatus={reviewStatus}
          onAdvance={onStatusChange}
          onRevoke={onStatusChange}
          canSetStatus={canSetStatus}
          busy={busy}
          disabled={statusActionsDisabled}
        />
      )}
    </div>
  );
}
